<template>
    <ModalBase
        :name="'cambiar-asiento-' + abonado.id"
        :title="'Cambiar asiento de ' + abonado.name + ' ' + abonado.surnames"
        @close="() => close(false)"
        @opened="getZonas"
    >
        <div data-cy="cambiar-asiento-modal" class="tw:flex tw:flex-col tw:gap-6">
            <Card title="Información del Abonado">
                <div class="tw:flex tw:flex-col tw:gap-3">
                    <div class="tw:flex tw:justify-between">
                        <span class="tw:font-semibold tw:text-gray-700">Nº de abonado:</span>
                        <span class="tw:text-gray-600">{{ abonado.n_abonado }}</span>
                    </div>
                    <div class="tw:flex tw:justify-between">
                        <span class="tw:font-semibold tw:text-gray-700">Nombre:</span>
                        <span class="tw:text-gray-600">{{ abonado.name }}</span>
                    </div>
                    <div class="tw:flex tw:justify-between">
                        <span class="tw:font-semibold tw:text-gray-700">Apellidos:</span>
                        <span class="tw:text-gray-600">{{ abonado.surnames }}</span>
                    </div>
                    <div class="tw:flex tw:justify-between">
                        <span class="tw:font-semibold tw:text-gray-700">Zona:</span>
                        <span class="tw:text-gray-600">{{ abonado.tiene_asiento ? abonado.asiento.region : abonado.plaza?.region_name }}</span>
                    </div>
                    <template v-if="abonado.tiene_asiento">
                        <div class="tw:flex tw:justify-between">
                            <span class="tw:font-semibold tw:text-gray-700">Fila:</span>
                            <span class="tw:text-gray-600">{{ abonado.asiento.row }}</span>
                        </div>
                        <div class="tw:flex tw:justify-between">
                            <span class="tw:font-semibold tw:text-gray-700">Butaca:</span>
                            <span class="tw:text-gray-600">{{ abonado.asiento.seat }}</span>
                        </div>
                    </template>
                    <div v-else-if="abonado.plaza" class="tw:flex tw:justify-between">
                        <span class="tw:font-semibold tw:text-gray-700">Plaza Nº:</span>
                        <span class="tw:text-gray-600">{{ abonado.plaza.numero }}</span>
                    </div>
                </div>
            </Card>

            <Card v-if="!hasSections" title="Selecciona una Zona">
                <div class="tw:flex tw:flex-col tw:gap-4">
                    <p class="tw:text-sm tw:text-gray-600">
                        <span v-if="!abonado.plaza">Al hacer click en el asiento este</span>
                        <span v-else>Al hacer click en la plaza esta</span>
                        se cambiará.
                    </p>
                    <div v-if="new_zona" class="tw:mb-2">
                        <Button variant="primary" @click="new_zona = null" :disabled="submitting || loading_zonas">
                            Cambiar zona
                        </Button>
                    </div>

                    <div v-if="!new_zona">
                        <div v-if="!loading_zonas">
                            <div v-if="zonas?.length" class="tw:flex tw:gap-4 tw:w-full">
                                <div class="tw:w-1/3">
                                    <TablaSectoresComponent :zonas="zonas" :cambio-zona="true" @goTo="(paso, zona) => { new_zona = { ...zona } }"/>
                                </div>
                                <div class="tw:w-2/3">
                                    <SvgComponent :zonas="zonas" :place="place" @clickZona="(zona) => { new_zona = { ...zona } }" />
                                </div>
                            </div>
                        </div>
                        <div v-else>
                            <div class="tw:flex tw:gap-4 tw:w-full tw:animate-pulse">
                                <!-- Left: sector table skeleton -->
                                <div class="tw:w-1/3 tw:space-y-3">
                                    <SkeletonItem height="tw:h-9" customClass="tw:w-full tw:rounded-lg" />
                                    <div class="tw:border tw:border-gray-200 tw:rounded-lg tw:overflow-hidden">
                                        <div class="tw:flex tw:items-center tw:px-4 tw:py-2 tw:border-b tw:border-gray-100 tw:bg-gray-50">
                                            <SkeletonItem height="tw:h-3" customClass="tw:w-5 tw:rounded-sm" />
                                            <SkeletonItem height="tw:h-3" customClass="tw:w-16 tw:ml-3" />
                                        </div>
                                        <div v-for="n in 4" :key="'sector-' + n" class="tw:flex tw:items-center tw:px-4 tw:py-3 tw:border-b tw:border-gray-100">
                                            <SkeletonItem height="tw:h-4" customClass="tw:w-4 tw:rounded-sm" />
                                            <SkeletonItem height="tw:h-3" customClass="tw:w-28 tw:ml-3" />
                                        </div>
                                    </div>
                                </div>
                                <!-- Right: map skeleton -->
                                <div class="tw:w-2/3 tw:border tw:border-gray-200 tw:rounded-lg tw:bg-gray-50 tw:flex tw:items-center tw:justify-center" style="min-height: 400px">
                                    <div class="tw:flex tw:flex-col tw:items-center tw:gap-3">
                                        <SkeletonItem height="tw:h-16" customClass="tw:w-16 tw:rounded-full tw:bg-gray-200" />
                                        <SkeletonItem height="tw:h-3" customClass="tw:w-36 tw:bg-gray-200" />
                                        <SkeletonItem height="tw:h-3" customClass="tw:w-24 tw:bg-gray-200" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>

            <Card v-else title="Selecciona una Zona">
                <div class="tw:flex tw:flex-col tw:gap-4">
                    <p class="tw:text-sm tw:text-gray-600">
                        Al hacer click en la zona esta se seleccionará para cambiar el asiento.
                    </p>
                    <div v-if="new_zona" class="tw:mb-2">
                        <Button variant="primary" @click="new_zona = null" :disabled="submitting || loading_zonas">
                            Cambiar zona
                        </Button>
                    </div>
                    <div v-if="!new_zona">
                        <CambiarAsientoSecciones @zonaSeleccionada="onZonaSeleccionada" />
                    </div>
                </div>
            </Card>

            <div v-if="new_zona">
                <Card title="Selecciona el nuevo asiento o plaza">
                    <template v-if="asientosLoading">
                        <div class="tw:space-y-4 tw:animate-pulse">
                            <!-- "Estado de las Plazas" header -->
                            <SkeletonItem height="tw:h-4" customClass="tw:w-40 tw:rounded" />
                            <!-- Color legend -->
                            <div class="tw:flex tw:items-center tw:gap-2">
                                <SkeletonItem height="tw:h-3" customClass="tw:w-56" />
                                <SkeletonItem height="tw:h-5" customClass="tw:w-5 tw:rounded" />
                            </div>
                            <!-- Map area -->
                            <div class="tw:border tw:border-gray-200 tw:rounded-lg tw:bg-gray-50 tw:flex tw:items-center tw:justify-center" style="height: 500px">
                                <div class="tw:flex tw:flex-col tw:items-center tw:gap-3">
                                    <SkeletonItem height="tw:h-16" customClass="tw:w-16 tw:rounded-full tw:bg-gray-200" />
                                    <SkeletonItem height="tw:h-3" customClass="tw:w-36 tw:bg-gray-200" />
                                    <SkeletonItem height="tw:h-3" customClass="tw:w-24 tw:bg-gray-200" />
                                </div>
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <CambiarAsientoNormal v-if="tipo_svg === 1" :zona="new_zona" :filas="filas" :loading="asientosLoading" @cambiarAsiento="onToggleSeleccion" />
                        <CambiarPlaza v-else :tipo_svg="tipo_svg" :zona="new_zona" :plaza_abonado="abonado.plaza" @cambiarPlaza="onToggleSeleccion" />
                    </template>
                </Card>
            </div>
        </div>
    </ModalBase>
</template>

<script setup>
    import { ref, computed } from 'vue';
    import store from '@/store';
    import { config } from '@/config.js';
    import { useToast } from '@/composables/common/useToast';
    import { useCambiarAsiento } from '@/composables/admin/abonados/useCambiarAsiento';
    import TablaSectoresComponent from '@/components/backend/stepper/components/TablaSectoresComponent';
    import SvgComponent from '@/components/svg/SvgComponent';
    import CambiarAsientoNormal from '@/modules/admin/abonados/components/cambiar-asiento/CambiarAsientoNormal';
    import CambiarAsientoSecciones from '@/modules/admin/abonados/components/cambiar-asiento/CambiarAsientoSecciones.vue';
    import CambiarPlaza from '@/modules/admin/abonados/components/cambiar-asiento/CambiarPlaza';
    import Card from '@/modules/admin/ui/Card.vue';
    import ModalBase from '@/modules/admin/ui/ModalBase.vue';
    import { SkeletonItem } from '@/modules/admin/ui/skeleton';

    const props = defineProps({
        abonado: {
            type: Object,
            required: true
        }
    });

    const emit = defineEmits(['close', 'asientoEditado']);
    const toast = useToast();
    const new_zona = ref(null);
    const loading_zonas = ref(false);
    const zonas = ref(null);

    // Setup composable for managing asientos
    const zonaRef = computed(() => new_zona.value);
    const { filas, loading: asientosLoading, submitting, toggleSeleccion } = useCambiarAsiento(zonaRef, {
        onAutoSelect: () => onToggleSeleccion(0, 0, 0)
    });

    // Computed
    const lista = computed(() => store.getters.getListaAbonados);
    const place = computed(() => lista.value.place);
    const tipoProcesoTaquilla = computed(() => place.value?.tipo_proceso_taquilla);
    const hasSections = computed(() => tipoProcesoTaquilla.value === 2);
    const tipo_svg = computed(() => {
        if (new_zona.value) {
            return parseInt(new_zona.value.tipo_svg);
        }
        return store.getters.getRecinto?.tipo_svg;
    });

    // Methods
    const onZonaSeleccionada = zona => {
        new_zona.value = { ...zona };
    };

    const close = cambiado => {
        new_zona.value = null;
        emit('close', 'cambiar-asiento-' + props.abonado.id, cambiado);
    };

    const getZonas = async() => {
        loading_zonas.value = true;

        try {
            const res = await store.dispatch(config.getVuexAlias('zonas.index'));
            zonas.value = res.data;
            await getZonasAnexas();
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Error al cargar las zonas';
            toast.error(errorMessage);
        } finally {
            loading_zonas.value = false;
        }
    };

    const getZonasAnexas = () => {
        const params = {
            filters: {},
            lista_id: lista.value.id,
            recinto_id: lista.value.place.id
        };

        return store.dispatch(config.getVuexAlias('back.abonos.taquilla.regiones-anexas'), params).then(res => {
            zonas.value = zonas.value.concat(res.data.data);
        }).catch(err => {
            console.error('Error in getZonasAnexas:', err);
        });
    };

    const onToggleSeleccion = (fila, asiento, ocupado, plaza_id = null) => {
        toggleSeleccion(fila, asiento, ocupado, plaza_id, {
            abonado_id: props.abonado.id,
            zona: new_zona.value,
            onSuccess: () => {
                emit('asientoEditado');
                close(true);
            }
        });
    };
</script>
